import { get } from 'lodash-es';
import moment, { Moment } from 'moment-timezone';

import { parseUtcTimestamp } from 'shared/utils';

// The following shorthand units of measurement types are supported.
// Reference: https://momentjs.com/docs/#/durations/creating/
type DurationUnitTypes = 'y' | 'M' | 'w' | 'd' | 'h' | 'm' | 's';

// Days, weeks, and years are consistent with the search DSL syntax, but differ
// from Moment's parser.
const periodMap = {
  D: 'd',
  W: 'w',
  Y: 'y',
};

const TEMPLATE_TS = 'timestamp';
const TEMPLATE_FORMAT = 'ms';
const TEMPLATE_UNIT = '[Y|M|W|D|h|m|s]';

export const TEMPLATE_TS_UNIT = `{{${TEMPLATE_TS}(\\[${TEMPLATE_FORMAT}\\])?([+-]\\d+${TEMPLATE_UNIT})*}}`;
export const URL_TEMPLATE_REGEX = new RegExp(
  `(\\S*){{value}}(\\S*)(${TEMPLATE_TS_UNIT})*(\\S*)({{sumoregion}})*(\\S*)`
);
export const URL_TEMPLATE_REGEX_SUMO_REGION_FIRST = new RegExp(
  `(\\S*)({{sumobaseurl}})(\\S*){{value}}(\\S*)(${TEMPLATE_TS_UNIT})*(\\S*)`
);

export const ALLOWED_URL_SCHEMES = ['http:', 'https:'];

export const isValidContextActionUrlTemplate = (template: string) => {
  // Handle {{sumobaseurl}} templates first - these resolve to https at runtime
  if (URL_TEMPLATE_REGEX_SUMO_REGION_FIRST.test(template)) {
    return true;
  }

  try {
    const urlTemplate = new URL(template);

    // Check if the URL scheme is in the allowed list
    if (!ALLOWED_URL_SCHEMES.includes(urlTemplate.protocol)) {
      return false;
    }

    return !!urlTemplate && URL_TEMPLATE_REGEX.test(template);
  } catch {
    // If URL creation fails, it's not a valid URL template
    return false;
  }
};

const getPeriod = (period: string): DurationUnitTypes =>
  periodMap.hasOwnProperty(period) ? periodMap[period] : period;

const hasFormat = (match: string) => /{{timestamp\[ms\]/g.test(match);

export const replaceTimestamps = (
  url: string,
  matchedTemplates: string[],
  timestamp: moment.Moment,
  operation: 'add' | 'subtract'
) => {
  // For each matched timestamp template, parse the duration, calculate the
  // new timestamp, and replace the template variable.
  for (let index = 0; index < matchedTemplates.length; index++) {
    const match = matchedTemplates[index];
    const [amount, period] = match
      .replace(
        new RegExp(`{{${TEMPLATE_TS}(\\[${TEMPLATE_FORMAT}\\])?[-+]|}}`, 'g'),
        ''
      )
      .split(new RegExp(`(${TEMPLATE_UNIT})`));

    const newTs = timestamp
      .clone()
      [operation](parseInt(amount, 10), getPeriod(period));
    const formattedTs = hasFormat(match)
      ? `${newTs.valueOf()}`
      : newTs.toISOString();
    url = url.replace(match, formattedTs);
  }

  return url;
};

export interface ContextData {
  [key: string]: any;
}

export const replaceContextProps = (url: string, contextData: ContextData) => {
  let success = true;
  let resultUrl = url;
  const matches = url.match(/{{\S*?}}/g) ?? [];
  matches.forEach((match) => {
    // Parse out the matched field name.
    const matchFieldName = match.replace('{{', '').replace('}}', '');

    // Lodash is used to support advanced nesting. Only include strings,
    // booleans, and numbers, all of which will be converted to strings.
    const matchFieldValue = get(contextData, matchFieldName);
    const isValid = ['string', 'boolean', 'number'].includes(
      typeof matchFieldValue
    );

    // Set `success` to false only if there isn't a value and the logic is
    // already successful.
    success = success && isValid;

    // Replace the original URL with the value. If a value doesn't exist, then
    // replace the template with an empty string. Ensure a string is used.
    resultUrl = resultUrl.replace(match, `${isValid ? matchFieldValue : ''}`);
  });

  return { success, url: resultUrl };
};

export const parseTimestampDeltas = (
  originalUrl: string,
  timestamp: number | string,
  unit: 's' | 'ms' = 's'
) => {
  // Parse timestamp.
  let parsedTs: Moment;
  if (typeof timestamp === 'number') {
    timestamp = timestamp as number;
    const momentTs = unit === 'ms' ? moment(timestamp) : moment.unix(timestamp);
    parsedTs = momentTs.utc();
  } else {
    parsedTs = parseUtcTimestamp(timestamp);
  }

  // Account for non-delta timestamps.
  let url = originalUrl.replace(
    new RegExp(`{{${TEMPLATE_TS}}}`, 'g'),
    parsedTs.toISOString()
  );
  url = url.replace(
    new RegExp(`{{${TEMPLATE_TS}\\[${TEMPLATE_FORMAT}\\]}}`, 'g'),
    parsedTs.format('x')
  );

  // Account for timestamps with positive deltas.
  const negDeltas = url.match(
    new RegExp(
      `{{${TEMPLATE_TS}(\\[${TEMPLATE_FORMAT}\\])?(-\\d+${TEMPLATE_UNIT})}}`,
      'g'
    )
  );
  if (negDeltas) {
    url = replaceTimestamps(url, negDeltas, parsedTs, 'subtract');
  }

  // Account for timestamps with positive deltas.
  const posDeltas = url.match(
    new RegExp(
      `{{${TEMPLATE_TS}(\\[${TEMPLATE_FORMAT}\\])?(\\+\\d+${TEMPLATE_UNIT})}}`,
      'g'
    )
  );
  if (posDeltas) {
    url = replaceTimestamps(url, posDeltas, parsedTs, 'add');
  }

  return url;
};
